import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
	setDebugMessage,
	setAlertMessage,
	setInfoMessage,
	setIsWaitModalShown,
} from './DebugSlice.js';
import {
	SOCKET_STATUS__CLOSED,
} from '../../constants/Chat';
import ChatManager from '../Middleware/ChatManager.js';
import { logout, replace, } from '../../RootNavigation.js';
import oneHatData from '@onehat/data';
import getTokenHeaders from '@onehat/ui/src/Functions/getTokenHeaders.js';
import setSecure from '@onehat/ui/src/Functions/setSecure';
import getSecure from '@onehat/ui/src/Functions/getSecure';
import deleteSecure from '@onehat/ui/src/Functions/deleteSecure';
import AppGlobals from '../../AppGlobals.js';
import moment from 'moment';
import _ from 'lodash';

const USER_CREDS = 'USER_CREDS-';

// async thunks
export const loginThunk = createAsyncThunk(
	'auth/login',
	async (args, { dispatch }) => {

		try {
			const {
					loginId,
					password,
					isValid,
				} = args;
			
			if (!isValid) {
				dispatch(setAlertMessage('Please completely fill out the form first.'));
				return;
			}

			dispatch(setIsWaitModalShown(true));

			let loginResult,
				savedCreds,
				isError = false;

			setSecure('loginId', loginId);
			const
				Users = oneHatData.getRepository('Users'),
				secureCredsKey = USER_CREDS + loginId;

			// Login user
			const params = {};
			params[AppGlobals.loginIdField] = loginId;
			params.password = password;
			loginResult = await Users.login(params)
				.catch(function (error) {
					dispatch(setAlertMessage(error.message));
					isError = true;
				});
			if (isError || !loginResult) {
				dispatch(setIsWaitModalShown(false));
				return;
			}

			// Login successful (otherwise would have thrown an Error)

			const {
					user,
					groups,
					permissions,
				} = loginResult;

			// Save user creds to secure store
			const creds = JSON.stringify({
				loginId,
				password,
				user,
				groups,
				permissions,
			});
			savedCreds = await setSecure(secureCredsKey, creds);

			const userEntity = await Users.createStandaloneEntity(user);

			await dispatch(setUserThunk(userEntity));
			await dispatch(setGroupsThunk(groups));
			await dispatch(setPermissionsThunk(permissions));
			
			dispatch(setAlertMessage(null));

			replace('AppNavigator'); // navigation

		} catch(error) {
			dispatch(setAlertMessage(error?.message));
		}
		dispatch(setIsWaitModalShown(false));

	}
);
export const setUserThunk = createAsyncThunk(
	'auth/setUser',
	async (user, { dispatch, getState }) => {
		dispatch(setUser(user?.getOriginalData()));
		
		let headers;
		if (user) {
			await setSecure('user', user);
			headers = getTokenHeaders();
		} else {
			await deleteSecure('user');
			headers = getTokenHeaders(true); // true for clearAll
		}
		if (!_.isEmpty(headers)) {
			oneHatData.setOptionsOnAllRepositories({ headers }); // already created
			oneHatData.setRepositoryGlobals({ headers }); // not yet created
		}

		// BEGIN MOD


		if (user) {
			// Set baseParams for ChatRecipients
			const ChatRecipients = oneHatData.getRepository('ChatRecipients');
			ChatRecipients._baseParams['conditions[chat_recipients__user_id]'] = user.id;
			ChatRecipients._baseParams.leftJoinWith = 'Recipients';
			await ChatRecipients.load();
		
			// setup ChatManager
			if (!AppGlobals.chatManager) {
				const chatManager = new ChatManager(getState);
				AppGlobals.chatManager = chatManager;
				chatManager.on('setChatIsSocketConnected', (arg) => dispatch(setChatIsSocketConnected(arg)));
				chatManager.on('setChatSocketStatus', (arg) => dispatch(setChatSocketStatus(arg)));
				chatManager.on('setChatCurrentConversation', (arg) => dispatch(setChatCurrentConversation(arg)));
				chatManager.on('setChatRecipientIsTyping', (arg) => dispatch(setChatRecipientIsTyping(arg)));
				chatManager.on('setChatBadgeCounts', (arg) => dispatch(setChatBadgeCounts(arg)));
				chatManager.on('setChatBadgeTotal', (arg) => dispatch(setChatBadgeTotal(arg)));
				chatManager.on('setChatRecipientOnlineStatuses', (arg) => dispatch(setChatRecipientOnlineStatuses(arg)));
				chatManager.on('setChatlastMessageReceived', (arg) => dispatch(setChatlastMessageReceived(arg)));
				chatManager.on('setAlertMessage', (arg) => dispatch(setAlertMessage(arg)));
			}
			AppGlobals.chatManager.connect();
			const conversations = {};
			_.each(ChatRecipients.entities, (entity) => {
				const
					recipient_id = entity.chat_recipients__recipient_id,
					conversation_num = entity.chat_recipients__conversation_num,
					full_name = entity.chat_recipients__full_name;
				conversations[conversation_num] = {
					recipient_id,
					conversation_num,
					full_name,
				};
			});
			AppGlobals.chatManager.setConversations(conversations);
			// END MOD
		}
	}
);
export const setGroupsThunk = createAsyncThunk(
	'auth/setGroups',
	async (groups, { dispatch }) => {

		dispatch(setGroups(groups));

		if (!_.isEmpty(groups)) {
			await setSecure('groups', groups);
		} else {
			await deleteSecure('groups');
		}
		
	}
);
export const setPermissionsThunk = createAsyncThunk(
	'auth/setPermissions',
	async (permissions, { dispatch }) => {

		if (_.isPlainObject(permissions)) {
			// login returns permissions as an object from the server,
			// so we need to convert it to an array
			permissions = _.map(permissions, (value, key) => {
				if (!parseInt(value)) {
					return null;
				}
				return key;
			});
		}
		dispatch(setPermissions(permissions));

		if (!_.isEmpty(permissions)) {
			await setSecure('permissions', permissions);
		} else {
			await deleteSecure('permissions');
		}
		
	}
);
export const logoutThunk = createAsyncThunk(
	'auth/logout',
	async (arg, { dispatch }) => {

		dispatch(setDebugMessage(null));
		dispatch(setAlertMessage(null));
		dispatch(setInfoMessage(null));
		await dispatch(setUserThunk(null));

		const Users = oneHatData.getRepository('Users');
		await Users.logout();

		// BEGIN MOD
		AppGlobals.chatManager.disconnect();
		// END MOD
		
		// router.replace('/');
		logout();
	}
);



export const appSlice = createSlice({
	name: 'app',
	initialState: {
		appState: 'active',
		user: null,
		groups: null,
		permissions: null,
		currentScreen: null,
		currentTab: 'App',

		// Chat
		chatIsSocketConnected: false,
		chatSocketStatus: SOCKET_STATUS__CLOSED,
		chatCurrentConversation: null,
		chatRecipientIsTyping: false,
		chatBadgeCounts: {},
		chatBadgeTotal: 0,
		chatRecipientOnlineStatuses: {},
		chatLastMessageReceived: null,
	},
	reducers: {
		setAppState: (state, action) => {
			state.appState = action.payload
		},
		setUser: (state, action) => {
			state.user = action.payload
		},
		setGroups: (state, action) => {
			state.groups = action.payload
		},
		setPermissions: (state, action) => {
			state.permissions = action.payload
		},
		setCurrentScreen: (state, action) => {
			state.currentScreen = action.payload
		},
		setCurrentTab: (state, action) => {
			state.currentTab = action.payload;
		},
		setHasNotificationPermissions: (state, action) => {
			state.hasNotificationPermissions = action.payload;
		},
		setChatIsSocketConnected: (state, action) => {
			state.chatIsSocketConnected = action.payload;
		},
		setChatSocketStatus: (state, action) => {
			state.chatSocketStatus = action.payload;
		},
		setChatCurrentConversation: (state, action) => {
			state.chatCurrentConversation = action.payload;
		},
		setChatRecipientIsTyping: (state, action) => {
			state.chatRecipientIsTyping = action.payload;
		},
		setChatBadgeCounts: (state, action) => {
			state.chatBadgeCounts = action.payload;
		},
		setChatBadgeTotal: (state, action) => {
			state.chatBadgeTotal = action.payload;
		},
		setChatRecipientOnlineStatuses: (state, action) => {
			state.chatRecipientOnlineStatuses = action.payload;
		},
		setChatlastMessageReceived: (state, action) => {
			state.chatLastMessageReceived = moment().format('YYYY-MM-DD HH:mm:ss');
		},
	}
});


// action definitions

export const {
	setAppState,
	setUser,
	setGroups,
	setPermissions,
	setCurrentScreen,
	setCurrentTab,
	setHasNotificationPermissions,
	setChatIsSocketConnected,
	setChatSocketStatus,
	setChatCurrentConversation,
	setChatRecipientIsTyping,
	setChatBadgeCounts,
	setChatBadgeTotal,
	setChatRecipientOnlineStatuses,
	setChatlastMessageReceived,
} = appSlice.actions;





// selectors

export const selectAppState = state => state.app.appState;
export const selectUser = state => state.app.user;
export const selectGroups = state => state.app.groups;
export const selectPermissions = state => state.app.permissions;
export const selectCurrentScreen = state => state.app.currentScreen;
export const selectCurrentTab = state => state.app.currentTab;
export const selectHasNotificationPermissions = state => state.app.hasNotificationPermissions;
export const selectChatIsSocketConnected = state => state.app.chatIsSocketConnected;
export const selectChatSocketStatus = state => state.app.chatSocketStatus;
export const selectChatCurrentConversation = state => state.app.chatCurrentConversation;
export const selectChatRecipientIsTyping = state => state.app.chatRecipientIsTyping;
export const selectChatBadgeCounts = state => state.app.chatBadgeCounts;
export const selectChatBadgeTotal = state => state.app.chatBadgeTotal;
export const selectChatRecipientOnlineStatuses = state => state.app.chatRecipientOnlineStatuses;
export const selectChatLastMessageReceived = state => state.app.chatLastMessageReceived;



export default appSlice.reducer;